import * as React from "react";
import { toast } from "react-toastify";
import type { SetURLSearchParams } from "react-router";
import type { BusinessReportOutcome } from "@/modules/snapshots/businessReports.types";
import type { Citation } from "@/modules/snapshots/grid/sidebar/qa/types";
import type { useSnapshotGridRowFetch } from "@/modules/snapshots/grid/dataSource";

interface Input {
  searchParams: URLSearchParams;
  setSearchParams: SetURLSearchParams;
  collectionId: string | undefined;
  subcollectionId: string | undefined;
  snapshotCompleted: BusinessReportOutcome[];
  fetchRowByResult: ReturnType<typeof useSnapshotGridRowFetch>;
}

/**
 * The outcome/result detail sidebar. It can be deep-linked once through `?outcomeId=&resultId=` (the params are
 * then removed from the URL) and opened from Q&A citations, including outcomes not loaded in the grid yet.
 */
export const useSnapshotGridDetailSidebar = ({
  searchParams,
  setSearchParams,
  collectionId,
  subcollectionId,
  snapshotCompleted,
  fetchRowByResult,
}: Input) => {
  const [sidebarOutcomeId, setSidebarOutcomeId] = React.useState<string | null>(searchParams.get("outcomeId"));
  const [sidebarResultId, setSidebarResultId] = React.useState<string | null>(searchParams.get("resultId"));
  const [citationSidebarOutcome, setCitationSidebarOutcome] = React.useState<BusinessReportOutcome | null>(null);

  React.useEffect(() => {
    setCitationSidebarOutcome(null);
  }, [collectionId, subcollectionId]);

  React.useEffect(() => {
    if (searchParams.has("outcomeId") || searchParams.has("resultId")) {
      const next = new URLSearchParams(searchParams);
      next.delete("outcomeId");
      next.delete("resultId");
      setSearchParams(next, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  const sidebarOutcome = React.useMemo(
    () =>
      sidebarOutcomeId
        ? (snapshotCompleted.find(o => o.id === sidebarOutcomeId) ??
          (citationSidebarOutcome?.id === sidebarOutcomeId ? citationSidebarOutcome : undefined))
        : undefined,
    [citationSidebarOutcome, sidebarOutcomeId, snapshotCompleted],
  );
  const sidebarResult = React.useMemo(
    () => (sidebarResultId && sidebarOutcome ? sidebarOutcome.result.find(r => r.id === sidebarResultId) : undefined),
    [sidebarResultId, sidebarOutcome],
  );

  const handleCitationClick = React.useCallback(
    async (citation: Citation) => {
      const outcome = snapshotCompleted.find(o => o.result.some(r => r.id === citation.result_id));
      if (outcome) {
        setCitationSidebarOutcome(null);
        setSidebarOutcomeId(outcome.id);
        setSidebarResultId(citation.result_id);
        return;
      }

      if (!collectionId) return;

      try {
        const fetchedOutcome = await fetchRowByResult(citation.result_id);
        setCitationSidebarOutcome(fetchedOutcome);
        setSidebarOutcomeId(fetchedOutcome.id);
        setSidebarResultId(citation.result_id);
      } catch {
        toast.error("Unable to open citation");
      }
    },
    [collectionId, fetchRowByResult, snapshotCompleted],
  );

  const closeSidebar = React.useCallback(() => {
    setSidebarOutcomeId(null);
    setSidebarResultId(null);
    setCitationSidebarOutcome(null);
  }, []);

  return {
    sidebarOutcomeId,
    setSidebarOutcomeId,
    setSidebarResultId,
    sidebarOutcome,
    sidebarResult,
    handleCitationClick,
    closeSidebar,
  };
};
